import React from 'react';
import { User, X, Lock, Ban } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BuzzerMode } from '../../../hooks/useBuzzerSignalR';
import { BUZZER_MODE_LABELS } from '../../../const/buzzer';
import BuzzerStage from './BuzzerStage';

/**
 * ready   — the buzzer is up for grabs.
 * pressed — this player already buzzed in this round.
 * locked  — lockout mode, someone else claimed the buzzer first.
 * blocked — this player buzzed in an earlier round and is out until a full reset.
 */
export type PlayerBuzzState = 'ready' | 'pressed' | 'locked' | 'blocked';

interface BuzzerPlayerProps {
  gameCode: string;
  playerName: string;
  mode: BuzzerMode;
  buzzState: PlayerBuzzState;
  myRank: number | null;
  /** Press already sent, waiting for the server to confirm it. */
  pressPending: boolean;
  statusText: string;
  onPress: () => void;
  onLeave: () => void;
}

const BUTTON_FACE: Record<PlayerBuzzState, { className: string; glow: string }> = {
  ready: {
    className: 'bg-gradient-to-br from-emerald-400 to-emerald-600 hover:scale-[1.03] active:scale-95',
    glow: 'rgba(16,185,129,0.35)',
  },
  pressed: {
    className: 'bg-gradient-to-br from-amber-400 to-amber-600 cursor-default',
    glow: 'rgba(250,204,21,0.40)',
  },
  locked: {
    className: 'bg-gradient-to-br from-slate-600 to-slate-800 cursor-not-allowed',
    glow: 'rgba(148,163,184,0.20)',
  },
  blocked: {
    className: 'bg-gradient-to-br from-rose-600 to-rose-800 cursor-not-allowed',
    glow: 'rgba(244,63,94,0.28)',
  },
};

const BuzzerPlayer: React.FC<BuzzerPlayerProps> = ({
  gameCode,
  playerName,
  mode,
  buzzState,
  myRank,
  pressPending,
  statusText,
  onPress,
  onLeave,
}) => {
  const face = BUTTON_FACE[buzzState];

  const faceContent = (() => {
    if (buzzState === 'pressed') {
      return <span className="text-7xl font-black tracking-widest text-white drop-shadow-md">#{myRank}</span>;
    }
    if (buzzState === 'blocked') {
      return (
        <>
          <Ban className="h-14 w-14 text-white/90" />
          <span className="text-2xl font-black tracking-widest text-white/90">KƏNARDA</span>
        </>
      );
    }
    if (buzzState === 'locked') {
      return (
        <>
          <Lock className="h-14 w-14 text-white/80" />
          <span className="text-2xl font-black tracking-widest text-white/80">KİLİDLİ</span>
        </>
      );
    }
    return <span className="text-6xl font-black tracking-widest text-white drop-shadow-md">SIX!</span>;
  })();

  return (
    <BuzzerStage>
      {/* ── Header ── */}
      <header className="relative z-10 flex items-center justify-between gap-3 px-4 pb-3 pt-[calc(1.25rem+env(safe-area-inset-top))] sm:px-10 sm:pt-[calc(1.75rem+env(safe-area-inset-top))]">
        <div className="flex min-w-0 flex-col">
          <span className="text-[11px] font-bold uppercase tracking-widest text-sky-300/60">Kod: {gameCode}</span>
          <div className="mt-0.5 flex items-center gap-2">
            <User className="h-4 w-4 shrink-0 text-sky-400" />
            <span className="truncate text-lg font-bold text-white">{playerName}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={onLeave}
          style={{
            borderRadius: '0.75rem',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.10)',
          }}
          className="flex h-9 shrink-0 items-center gap-1.5 px-3 text-sm font-semibold text-white/60 transition hover:text-white sm:h-10"
        >
          <X className="h-4 w-4" />
          Çıxış
        </button>
      </header>

      {/* ── Main ── */}
      <main className="relative z-10 flex flex-1 flex-col items-center justify-center gap-10 px-4 pb-[calc(2rem+env(safe-area-inset-bottom))]">
        {/* Ambient glow disc */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute"
          style={{
            width: 'min(70vmin, 460px)',
            height: 'min(70vmin, 460px)',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${face.glow} 0%, transparent 68%)`,
            transition: 'background 0.5s ease',
            filter: 'blur(28px)',
          }}
        />

        <button
          type="button"
          onClick={onPress}
          disabled={buzzState !== 'ready' || pressPending}
          className={cn(
            'relative flex shrink-0 flex-col items-center justify-center gap-2 rounded-full border-8 border-black/20 shadow-[0_10px_40px_rgba(0,0,0,0.55)] transition-all duration-150',
            face.className
          )}
          style={{ width: 'min(58vmin, 320px)', height: 'min(58vmin, 320px)' }}
        >
          {faceContent}
        </button>

        <p
          className={cn(
            'relative max-w-md text-center text-2xl font-bold',
            buzzState === 'pressed' && 'text-amber-300',
            buzzState === 'blocked' && 'text-rose-300',
            buzzState === 'locked' && 'text-slate-300',
            buzzState === 'ready' && 'text-white'
          )}
        >
          {statusText}
        </p>

        <p className="relative text-center text-xs text-sky-200/40">{BUZZER_MODE_LABELS[mode].hint}</p>
      </main>
    </BuzzerStage>
  );
};

export default BuzzerPlayer;
